import { useState, useEffect } from 'react';
import { showAlert } from '@/components/AppDialog';
import { doc, getDoc } from 'firebase/firestore';
import { saveAdminConfig } from '@/utils/adminConfig';
import { db } from '../firebase';
import { logAdminAction } from '@/utils/audit';
import AdminLayout from '../components/AdminLayout';
import { Save, CheckCircle2, AlertTriangle, Plus, Trash2, Info, Rocket } from 'lucide-react';

/* Admin editor for the Consulting pricing page — Firestore `consultingPricing/current`.
   ConsultingPricing.tsx (/capital-raising/pricing) reads this doc live (onSnapshot),
   so saves go live instantly. Prices are free-text display strings (e.g. "$2,999"). */

type Rung = { label: string; sub: string; price: string };
type Tier = { tag: string; name: string; desc: string; price: string; features: string[]; pop: boolean; ctaDark: boolean };
type Faq = { q: string; a: string };
type Model = { ladder: Rung[]; tiers: Tier[]; faqs: Faq[] };

const DEFAULT: Model = {
  ladder: [
    { label: 'Free score', sub: 'See your gaps', price: '$0' },
    { label: 'Living score', sub: 'Track & 80% alerts', price: '$7.99 / mo' },
    { label: 'Capital Assessment', sub: '40-min advisor session', price: '$99 · credited' },
  ],
  tiers: [
    { tag: 'Get the basics right', name: 'Foundations', desc: 'The core building blocks every raise needs, done properly.', price: '$2,999', features: ['Investor-grade financial model', 'Pitch narrative & core deck', 'Readiness plan to lift your score'], pop: false, ctaDark: false },
    { tag: 'Get to 80%', name: 'Investor-Ready', desc: 'Everything needed to walk into the room ready to raise.', price: '$6,999', features: ['Everything in Foundations', 'Full pitch deck & data room', 'KPI dashboard & hands-on score lift to 80%'], pop: true, ctaDark: true },
    { tag: 'Be raise-ready', name: 'Raise-Ready', desc: 'The full programme, plus investor targeting and prep.', price: '$11,999', features: ['Everything in Investor-Ready', 'Investor targeting & intro prep', 'Mock pitches & dedicated advisor'], pop: false, ctaDark: false },
  ],
  faqs: [
    { q: 'Is the readiness score really free?', a: 'Yes. Your static readiness score and dashboard are free, so you can see exactly where you stand before spending anything.' },
    { q: 'What do I get for $7.99 a month?', a: 'The living score: your readiness score tracked over time, with movement nudges and an alert when you cross the 80% investor-ready line.' },
    { q: "What's included in the $99 Capital Assessment?", a: 'A focused 40-minute session with an advisor who reviews your readiness and maps your gaps. The $99 is credited against any package you take afterwards.' },
    { q: 'Can I buy just one package?', a: 'Yes — each package stands on its own. Most founders start with Foundations or Investor-Ready and add more only if they need it.' },
    { q: "Do you guarantee I'll raise or get funded?", a: "No — no one credibly can. We maximise your readiness and the strength of your materials." },
    { q: 'Which markets do you cover?', a: 'We work with founders across MENA, the UK and the US.' },
  ],
};

const clone = (m: Model): Model => JSON.parse(JSON.stringify(m));
const inputCls = 'w-full border-2 border-gray-200 rounded-lg px-3 py-2 text-sm font-semibold bg-blue-50/60 text-gray-900 focus:outline-none focus:border-blue-400';
const lbl = 'text-[11px] font-bold text-gray-400 uppercase tracking-wide block mb-1';

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="h-1.5 bg-gradient-to-r from-blue-500 to-blue-600" />
      <div className="p-5">
        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}
const AddBtn = ({ onClick, label }: { onClick: () => void; label: string }) => (
  <button onClick={onClick} className="flex items-center gap-1.5 text-blue-700 hover:text-blue-800 font-bold text-xs mt-3"><Plus className="w-4 h-4" /> {label}</button>
);
const DelBtn = ({ onClick }: { onClick: () => void }) => (
  <button onClick={onClick} className="text-gray-300 hover:text-red-500 transition-colors p-1.5" title="Remove"><Trash2 className="w-4 h-4" /></button>
);

export default function AdminConsultingPricing() {
  const [m, setM] = useState<Model>(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const snap = await getDoc(doc(db, 'consultingPricing', 'current'));
        if (snap.exists()) {
          const d = snap.data() as Partial<Model>;
          setM({
            ladder: Array.isArray(d.ladder) && d.ladder.length ? d.ladder : DEFAULT.ladder,
            tiers: Array.isArray(d.tiers) && d.tiers.length ? d.tiers : DEFAULT.tiers,
            faqs: Array.isArray(d.faqs) && d.faqs.length ? d.faqs : DEFAULT.faqs,
          });
        }
      } catch (e: any) {
        console.error('Failed to load consultingPricing/current:', e);
        if (e?.code === 'permission-denied') setLoadError('Firestore permission denied — showing defaults. Sign in as a Firebase-authenticated admin to save.');
      } finally { setLoading(false); }
    })();
  }, []);

  const patch = (fn: (draft: Model) => void) => { setM(prev => { const d = clone(prev); fn(d); return d; }); setSavedAt(null); };

  const handleSave = async () => {
    setSaving(true);
    try {
      await saveAdminConfig('consultingPricing/current', { ...m });
      setSavedAt(new Date().toLocaleTimeString());
      logAdminAction('config.update', 'consultingPricing/current');
    } catch (e: any) {
      console.error('Save failed:', e);
      showAlert(e?.code === 'permission-denied' ? 'Save failed — permission denied. This editor needs a Firebase-authenticated admin session.' : 'Save failed — check console.');
    } finally { setSaving(false); }
  };
  const handleReset = () => { setM(clone(DEFAULT)); setSavedAt(null); };

  const LAYOUT = {
    section: 'Pricing',
    title: 'Consulting Pricing',
    icon: Rocket,
    subtitle: 'Entry ladder, packages & FAQs — edits go live on the consulting pricing page instantly.',
  };

  if (loading) return <AdminLayout {...LAYOUT} loading loadingLabel="Loading consulting pricing…" />;

  return (
    <AdminLayout {...LAYOUT} spaced>
      {loadError && (
        <div className="flex gap-2 items-start bg-red-50 border border-red-300 rounded-xl px-4 py-3 text-xs font-bold text-red-800">
          <AlertTriangle className="w-4 h-4 flex-none stroke-red-500 mt-0.5" /><span>{loadError}</span>
        </div>
      )}
      <div className="flex gap-2 items-start bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 text-xs font-bold text-blue-800">
        <Info className="w-4 h-4 flex-none stroke-blue-500 mt-0.5" />
        <span>Prices are display text (e.g. <b>$2,999</b>, <b>$7.99 / mo</b>). Features: one per line. Changes are live to customers the moment you save.</span>
      </div>

      {/* Entry ladder */}
      <Card title="Entry ladder (free → living → assessment)">
        <div className="space-y-2">
          {m.ladder.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-3 items-center">
              <input value={r.label} onChange={e => patch(d => { d.ladder[i].label = e.target.value; })} className={inputCls} placeholder="Label" />
              <input value={r.sub} onChange={e => patch(d => { d.ladder[i].sub = e.target.value; })} className={inputCls} placeholder="Sub-text" />
              <input value={r.price} onChange={e => patch(d => { d.ladder[i].price = e.target.value; })} className={inputCls} placeholder="Price" />
              <DelBtn onClick={() => patch(d => { d.ladder.splice(i, 1); })} />
            </div>
          ))}
        </div>
        <AddBtn label="Add rung" onClick={() => patch(d => { d.ladder.push({ label: '', sub: '', price: '' }); })} />
      </Card>

      {/* Packages */}
      <Card title="Consulting packages">
        <div className="space-y-4">
          {m.tiers.map((t, i) => (
            <div key={i} className="border-2 border-gray-100 rounded-xl p-4 relative">
              <div className="absolute top-3 right-3"><DelBtn onClick={() => patch(d => { d.tiers.splice(i, 1); })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div><label className={lbl}>Name</label><input value={t.name} onChange={e => patch(d => { d.tiers[i].name = e.target.value; })} className={inputCls} /></div>
                <div><label className={lbl}>Tag</label><input value={t.tag} onChange={e => patch(d => { d.tiers[i].tag = e.target.value; })} className={inputCls} /></div>
                <div><label className={lbl}>Price</label><input value={t.price} onChange={e => patch(d => { d.tiers[i].price = e.target.value; })} className={inputCls} placeholder="$2,999" /></div>
              </div>
              <div className="mt-3"><label className={lbl}>Description</label><input value={t.desc} onChange={e => patch(d => { d.tiers[i].desc = e.target.value; })} className={inputCls} /></div>
              <div className="mt-3"><label className={lbl}>Features (one per line)</label>
                <textarea value={t.features.join('\n')} onChange={e => patch(d => { d.tiers[i].features = e.target.value.split('\n').map(s => s.trimEnd()).filter(Boolean); })} rows={Math.max(3, t.features.length)} className={`${inputCls} font-normal leading-relaxed`} /></div>
              <div className="flex gap-5 mt-3">
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><input type="checkbox" checked={t.pop} onChange={e => patch(d => { d.tiers[i].pop = e.target.checked; })} /> Most popular</label>
                <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600"><input type="checkbox" checked={t.ctaDark} onChange={e => patch(d => { d.tiers[i].ctaDark = e.target.checked; })} /> Dark CTA button</label>
              </div>
            </div>
          ))}
        </div>
        <AddBtn label="Add package" onClick={() => patch(d => { d.tiers.push({ tag: '', name: 'New package', desc: '', price: '', features: [], pop: false, ctaDark: false }); })} />
      </Card>

      {/* FAQs */}
      <Card title="FAQs">
        <div className="space-y-3">
          {m.faqs.map((f, i) => (
            <div key={i} className="border-b border-gray-100 pb-3 last:border-0 last:pb-0">
              <div className="flex gap-2 items-start">
                <div className="flex-1 space-y-2">
                  <input value={f.q} onChange={e => patch(d => { d.faqs[i].q = e.target.value; })} className={inputCls} placeholder="Question" />
                  <textarea value={f.a} onChange={e => patch(d => { d.faqs[i].a = e.target.value; })} rows={2} className={`${inputCls} font-normal leading-relaxed`} placeholder="Answer" />
                </div>
                <DelBtn onClick={() => patch(d => { d.faqs.splice(i, 1); })} />
              </div>
            </div>
          ))}
        </div>
        <AddBtn label="Add FAQ" onClick={() => patch(d => { d.faqs.push({ q: '', a: '' }); })} />
      </Card>

      <div className="flex items-center gap-3 flex-wrap pb-8">
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-extrabold text-sm px-5 py-2.5 rounded-xl shadow-md transition-all hover:-translate-y-0.5 active:translate-y-0">
          <Save className="w-4 h-4" />{saving ? 'Saving…' : 'Save consulting pricing'}
        </button>
        <button onClick={handleReset} className="border border-gray-200 bg-white text-gray-600 font-bold text-sm px-4 py-2.5 rounded-xl hover:bg-gray-50 transition-colors">Reset to defaults</button>
        {savedAt && <span className="flex items-center gap-1.5 text-green-600 font-bold text-xs"><CheckCircle2 className="w-4 h-4" /> Saved · live to customers as of {savedAt}</span>}
      </div>
    </AdminLayout>
  );
}
